import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  GraduationCap,
  Menu,
  Search,
  X,
} from "lucide-react";
import { SidebarLogoutButton, SidebarNav } from "./sidebar";
import BillingReminder from "./DashboardComponent/BillingReminder";

const getDisplayName = ({ firstname, lastname, username }) => {
  const fullName = `${firstname || ""} ${lastname || ""}`.trim();
  return fullName || username || "Student";
};

export default function Headerdash({ showBillingReminder = false, onReminderHeightChange }) {
  const [userData, setUserData] = useState({
    username: "",
    firstname: "",
    lastname: "",
  });

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      const response = await fetch("/logout", {
        method: "GET",
        credentials: "include",
      });

      if (response.status === 200) {
        navigate("/login");
        console.log("Logout Successfully");
      } else {
        console.error("Logout Failed");
      }
    } catch (error) {
      console.error("Error During Logout:", error);
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await fetch("/api/user-info", {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.status === 200) {
          const data = await response.json();
          setUserData({
            username: data.username,
            firstname: data.firstname,
            lastname: data.lastname,
          });
        } else {
          console.error("Failed to fetch User data");
        }
      } catch (error) {
        console.error("Error fetching User data:", error);
      }
    };

    fetchUserData();
  }, []);

  const displayName = getDisplayName(userData);

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-20 border-b border-slate-200 bg-gray-50/95 backdrop-blur">
        {showBillingReminder && <BillingReminder onHeightChange={onReminderHeightChange} />}
        <div className="mx-auto w-full px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              type="button"
              className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-white text-gray-600 shadow-sm transition hover:text-purple-700 sm:hidden"
              onClick={() => setIsSidebarOpen(true)}
              aria-label="Open navigation"
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </button>

            <div className="hidden min-w-0 flex-1 sm:block">
              <p className="truncate text-xs font-semibold uppercase text-purple-700">
                Welcome back
              </p>
              <h1 className="truncate text-xl font-extrabold text-gray-900">
                {displayName}
              </h1>
            </div>

            <div className="flex flex-1 items-center justify-end gap-2 sm:flex-none">
              <button
                type="button"
                className="hidden h-10 w-10 shrink-0 place-items-center rounded-md bg-white text-gray-600 shadow-sm transition hover:text-purple-700 sm:grid"
                aria-label="Search"
              >
                <Search className="h-5 w-5" aria-hidden="true" />
              </button>

              <a
                href="/practice"
                className="hidden h-10 w-10 shrink-0 place-items-center rounded-md bg-white text-gray-600 shadow-sm transition hover:text-purple-700 sm:grid"
                aria-label="Academy"
              >
                <GraduationCap className="h-5 w-5" aria-hidden="true" />
              </a>

              <button
                type="button"
                className="grid h-10 w-10 shrink-0 place-items-center rounded-md bg-white text-gray-600 shadow-sm transition hover:text-purple-700"
                aria-label="Notifications"
              >
                <Bell className="h-5 w-5" aria-hidden="true" />
              </button>

              <button
                type="button"
                className="group flex min-w-0 shrink-0 items-center rounded-md bg-white p-1.5 shadow-sm transition hover:ring-1 hover:ring-purple-100"
                aria-label="User menu"
              >
                <img
                  alt=""
                  src="https://www.shutterstock.com/image-vector/user-profile-icon-vector-avatar-600nw-2220431045.jpg"
                  className="h-9 w-9 rounded-full object-cover"
                />

                <span className="ms-2 hidden max-w-44 text-left text-xs sm:block">
                  <strong className="block truncate font-semibold text-gray-800">
                    {displayName}
                  </strong>
                  <span className="block truncate text-gray-500">
                    {userData.username}
                  </span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {isSidebarOpen && (
        <div className="fixed inset-0 z-30 min-h-screen bg-gray-900/50 sm:hidden">
          <div className="flex h-screen max-h-screen w-[min(20rem,88vw)] flex-col bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-4">
              <a
                className="flex items-center gap-3"
                href="/dashboard"
                onClick={() => setIsSidebarOpen(false)}
              >
                <img
                  className="h-10 w-auto max-w-[150px] object-contain"
                  src="/image/prep2.png"
                  alt="PrepNexis"
                />
              </a>
              <button
                type="button"
                className="grid h-10 w-10 place-items-center rounded-md bg-slate-50 text-gray-600 transition hover:text-purple-700"
                onClick={() => setIsSidebarOpen(false)}
                aria-label="Close navigation"
              >
                <X className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            <div className="border-b border-slate-100 bg-slate-50 px-4 py-4">
              <p className="truncate text-sm font-bold text-gray-900">
                {displayName}
              </p>
              <p className="truncate text-xs text-gray-500">
                {userData.username || "Your study workspace"}
              </p>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5">
              <SidebarNav
                compact
                onNavigate={() => setIsSidebarOpen(false)}
              />
            </div>

            <div className="border-t border-slate-100 p-3">
              <SidebarLogoutButton onLogout={handleLogout} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
